import {useCallback, useEffect, useMemo} from 'react'

import {useAppDispatch, useAppSelector} from '../../../hooks/react-redux'
import {useSetUserProperty} from '../../../hooks/useSetUserProperty'
import {useSidebarPinned} from '../../App/SidebarPanel/SidebarPanel.hooks'
import {sidebarSettings} from '../../App/SidebarPanel/SidebarPanel.slices'
import {getSidebarPinnedProperty, SidebarType} from '../../App/SidebarPanel/SidebarPanel.types'
import useLocalStorageValue from '../../pages/PipelinesPages/hooks/useLocalStorageValue'

import {
  aiAssistantChatButtonCloseEvent,
  aiAssistantChatButtonOpenEvent,
  maxWindowWidth,
  minWindowWidth,
} from './AiAssistantChatWrapper.const'

function useAiAssistantChatWrapper() {
  const dispatch = useAppDispatch()
  const collapsedMap = useAppSelector(state => state.sidebarSettings.collapsed)
  const agentsPinned = useSidebarPinned(SidebarType.AGENTS)
  const queuePinned = useSidebarPinned(SidebarType.QUEUE)
  const projectsPinned = useSidebarPinned(SidebarType.PROJECTS)
  const [prevStateCollapsed, setPrevStateCollapsed] = useLocalStorageValue(
    'jb.teamcity.pipelines.settingsSidebar.prevStateCollapsed',
  )
  const [prevStatePinned, setPrevStatePinned] = useLocalStorageValue(
    'jb.teamcity.pipelines.settingsSidebar.prevStatePinned',
  )
  const setUserProperty = useSetUserProperty()
  const pinnedSnapshot = useMemo(
    () => ({
      [SidebarType.AGENTS]: agentsPinned,
      [SidebarType.QUEUE]: queuePinned,
      [SidebarType.PROJECTS]: projectsPinned,
    }),
    [agentsPinned, queuePinned, projectsPinned],
  )
  const closeAiAssistantChatButtonHandler = useCallback(() => {
    if (!prevStateCollapsed || !prevStatePinned) {
      return
    }
    const prevCollapsedMap = JSON.parse(prevStateCollapsed)
    const prevPinnedMap = JSON.parse(prevStatePinned)
    Object.values(SidebarType).forEach(id => {
      const nextPinned = prevPinnedMap?.[id]
      if (nextPinned != null) {
        setUserProperty(getSidebarPinnedProperty(id), String(nextPinned))
      }
      const nextCollapsed = prevCollapsedMap?.[id]
      dispatch(
        sidebarSettings.actions.setSidebarCollapsed({
          id,
          collapsed: nextCollapsed ?? !nextPinned,
        }),
      )
    })
  }, [prevStateCollapsed, prevStatePinned, dispatch, setUserProperty])
  const openAiAssistantChatButtonHandler = useCallback(() => {
    const windowWidth = window.innerWidth
    const isMoreThanMinWidth = windowWidth > minWindowWidth && windowWidth < maxWindowWidth

    setPrevStateCollapsed(JSON.stringify(collapsedMap))
    setPrevStatePinned(JSON.stringify(pinnedSnapshot))

    if (isMoreThanMinWidth) {
      Object.values(SidebarType).forEach(id => {
        setUserProperty(getSidebarPinnedProperty(id), 'false')
        dispatch(
          sidebarSettings.actions.setSidebarCollapsed({
            id,
            collapsed: true,
          }),
        )
      })
    }
  }, [
    collapsedMap,
    pinnedSnapshot,
    dispatch,
    setPrevStateCollapsed,
    setPrevStatePinned,
    setUserProperty,
  ])

  useEffect(() => {
    window.addEventListener(aiAssistantChatButtonCloseEvent, closeAiAssistantChatButtonHandler)
    window.addEventListener(aiAssistantChatButtonOpenEvent, openAiAssistantChatButtonHandler)

    return () => {
      window.removeEventListener(aiAssistantChatButtonCloseEvent, closeAiAssistantChatButtonHandler)
      window.removeEventListener(aiAssistantChatButtonOpenEvent, openAiAssistantChatButtonHandler)
    }
  }, [closeAiAssistantChatButtonHandler, openAiAssistantChatButtonHandler])
}

export default useAiAssistantChatWrapper
